import * as yup from 'yup';
import { DROPDOWN_OPTIONS as CMA_OPTIONS } from '../../../AdviserSettings/Page/CustomModelAssumptions/constants';
import { CurrentDistributionOrderDTO } from '../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';
import { RiskToleranceQuestionnaireType } from '../../../CommonSelectors/CommonArea.types';
import {
  MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES,
  ManualAccountsAssetAllocationStrategyIdDTO,
} from '../../../ManualAccountsAssetAllocation/types';
import { PlanStartYearDTO, SettingsDTO } from '../../api/useClientSettingsQuery';

export interface FormValues extends SettingsDTO {
  AccountDistributionOrder: CurrentDistributionOrderDTO;
}

export const DROPDOWN_OPTIONS = {
  remainingCashflowModeOptions: [
    { label: 'Spend', value: 0 },
    { label: 'Save', value: 1 },
  ],

  portfolioReturnsOptions: [
    { label: 'Use Advisor settings', value: null },
    { label: 'Weighted Average', value: 0 },
    { label: 'Hypothetical', value: 1 },
  ],

  assetBucketingOptions: [
    { label: 'Use Advisor settings', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ],

  assumeCurrentTaxRateProvisionsExpireOptions: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ],

  beFi20Options: [
    { label: 'Use Advisor settings', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ],

  pulseCheckOptions: [
    { label: 'Use Advisor settings', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ],

  riskToleranceWorkflowOptions: [
    {
      label: 'Use Advisor settings',
      value: null,
    },
    { label: 'Orion Questionnaire', value: RiskToleranceQuestionnaireType.Advizr },
    { label: '3D Risk Profile', value: RiskToleranceQuestionnaireType.HiddenLevers },
  ],

  capitalMarketAssumptionsOptions: [
    ...CMA_OPTIONS,
    {
      label: 'Use Advisor settings',
      value: null,
    },
  ],

  planStartYearOptions: [
    {
      label: 'Use Advisor settings',
      value: null,
    },
    {
      label: `Current Calendar Year (${new Date().getFullYear()})`,
      value: PlanStartYearDTO.CurrentYear,
      subLabel: `Starts with this year's remaning months`,
    },
    {
      label: `Next Full Year (${new Date().getFullYear() + 1})`,
      value: PlanStartYearDTO.NextYear,
      subLabel: 'Begins projections in next full year',
    },
  ],

  defaultAssetAllocationOptions: [
    { label: 'Cash', value: ManualAccountsAssetAllocationStrategyIdDTO.Cash },
    { label: 'Use CMAs', value: ManualAccountsAssetAllocationStrategyIdDTO.CMA },
    { label: 'Use My Models', value: ManualAccountsAssetAllocationStrategyIdDTO.Model },
    { label: 'Use Advisor settings', value: ManualAccountsAssetAllocationStrategyIdDTO.Inherit },
  ],
};

export const Validation = yup.object().shape({
  MonteCarloSuccessRange: yup
    .number()
    .typeError('The value must be between 50% and 99%')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .min(50, 'The value must be between 50% and 99%')
    .max(99, 'The value must be between 50% and 99%'),
  ExcessCashFlowPercentToSave: yup
    .number()
    .typeError('The value must be between 0% and 100%')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .min(0, 'The value must be between 0% and 100%')
    .max(100, 'The value must be between 0% and 100%'),
  ManualAccountsAssetAllocation: yup.object().shape({
    strategy: yup
      .mixed<ManualAccountsAssetAllocationStrategyIdDTO>()
      .oneOf(
        Object.values(
          ManualAccountsAssetAllocationStrategyIdDTO,
        ) as ManualAccountsAssetAllocationStrategyIdDTO[],
      )
      .required('Strategy is required'),
    details: yup.mixed().when('strategy', {
      is: (strategy: ManualAccountsAssetAllocationStrategyIdDTO) =>
        strategy === ManualAccountsAssetAllocationStrategyIdDTO.CMA ||
        strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model,
      then: yup
        .object()
        .test(
          'non-empty-allocations',
          'Asset allocation is required for each tax status',
          value => {
            if (!value) {
              return false;
            }

            return MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES.every(taxStatusId => {
              return !!value[taxStatusId];
            });
          },
        )
        .required('Asset allocation is required for each tax status')
        .typeError('Asset allocation is required for each tax status'),
      otherwise: yup.mixed().nullable(),
    }),
  }),
});

export const TOAST_SUCCESS_MESSAGE = 'Settings were successfully saved';
